import { useEffect } from 'react'
import { useTheme, styled } from '@mui/material'
import {
  Route,
  Router,
  Routes,
  Outlet,
  Section,
  useNavigate,
  resetPathname,
  RouterPanel,
  usePanelHistoryTop,
} from 'weblite-router'
import { safeExitFullscreen } from '@weblite/logic-toolkit'
import { ConditionalHooksPool } from '@weblite/use-conditional-hook'
import { handleLink } from '$logic/router/url'
import { useIsLoggedIn } from '$logic/auth/react-hook'
import MultiTab from '$comp/error-pages/multi-tab/multi-tab'
import { useIsAppLoaded, useIsMultiTab } from '$comp/main/react-hook'
import { isPhoneOrTablet, SIMULATED_PHONE_WIDTH } from '$func/environmentInfo'
import { ToastProvider } from '$comp/snackbars'
import {
  OverlayWappPanel,
  ModalsPanel,
  StoryPanel,
} from '$scenes/router-panels'
import { App } from '$scenes/components/app'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { ForceUpdateTwa } from '$comp/force-update-twa/force-update-twa'
import { InstallTwa } from '$comp/install-twa'
import {
  useForceInstallTwaPageIsOpen,
  useNeedForceUpdateTwa,
} from '$logic/client-device/react-hook'
import { initializeCssVars } from '$setup/initialize-css-vars'
import { useStartup } from '$hooks/use-startup'
import { Auth } from './auth'
import { checkForSynchronizePersistToResolveRace } from '$func/sync-versions'
import { sendSignOutByServerAnalyticsEvent } from '$logic/auth/sign-out-analytics'
import { EmojiPickerPopover } from '$comp/modals/emoji-picker/emoji-picker'

const PanelsContainerRoot = styled('div')`
  width: 100%;
  height: 100%;
  position: relative;

  transition: background-color 250ms;
  background-color: var(--safe-area-color, #fff);
  --hyper-text-color: ${({ theme }) => theme.palette.primary500};
`

function useSetRootBgColor() {
  const theme = useTheme()
  const location = usePanelHistoryTop('root')?.location

  useEffect(() => {
    const bg =
      !location || location === 'root/auth/signIn'
        ? theme.palette.primary500
        : theme.palette.neutral200

    const rootElem = document.getElementById('root')
    if (rootElem) rootElem.style.backgroundColor = bg
  }, [location])
}

function PanelsContainer() {
  useSetRootBgColor()

  const isDesktop = useIsDesktopSize()
  const location = usePanelHistoryTop('root')?.location

  useEffect(() => {
    initializeCssVars()
  }, [])

  return (
    <PanelsContainerRoot
      style={{
        ...(!isPhoneOrTablet && {
          width: SIMULATED_PHONE_WIDTH,
          margin: 'auto',
          boxShadow: 'rgb(0 0 0 / 5%) 0px 0px 20px 5px',
          overflow: 'hidden',
        }),

        ...(isDesktop && {
          width: '100%',
          maxWidth: location?.startsWith('root/auth') ? 'unset' : '1680px',
        }),
      }}
    >
      <Section name="overlay">
        <OverlayWappPanel.Portal data-name="overlayWapp">
          <RouterPanel />
        </OverlayWappPanel.Portal>

        <ModalsPanel.Portal data-name="modals">
          <RouterPanel />
        </ModalsPanel.Portal>
      </Section>

      <Section name="story" initialPanel="story">
        <StoryPanel.Portal data-name="story">
          <RouterPanel />
        </StoryPanel.Portal>
      </Section>

      <Outlet />

      <ToastProvider />
    </PanelsContainerRoot>
  )
}

export function Root() {
  const isLoaded = useIsAppLoaded()
  const isRunningInAnotherTab = useIsMultiTab()
  const needForceInstallTwa = useForceInstallTwaPageIsOpen()
  const needForceUpdateTwa = useNeedForceUpdateTwa()
  const isAuthenticated = useIsLoggedIn()
  const navigate = useNavigate()

  const isDesktop = useIsDesktopSize()

  useEffect(() => {
    if (isLoaded) {
      handleLink(window.location.href)
        .then(resetPathname)
        .then(checkForSynchronizePersistToResolveRace)

      // NOTE: If user refreshes app in fullscreen mode, it won't exit by default in some devices. so we need to call exitFullscreen to reset it
      safeExitFullscreen()
    }
  }, [isLoaded])

  useEffect(() => {
    if (isAuthenticated) {
      navigate('root/app', { absolute: true })
    } else {
      sendSignOutByServerAnalyticsEvent()
    }
  }, [isAuthenticated])

  useStartup()

  if (!isLoaded) return null

  return (
    <>
      <ConditionalHooksPool />
      <MultiTab isOpen={isRunningInAnotherTab} />
      <InstallTwa isOpen={Boolean(needForceInstallTwa)} mode="INSTALL" />
      <ForceUpdateTwa isOpen={needForceUpdateTwa} />
      <EmojiPickerPopover />
      <Router
        initialItem={{ location: 'root/auth/signIn' }}
        options={{ disableAnimations: isDesktop }}
      >
        <Routes>
          <Route name="root" element={<PanelsContainer />}>
            <Route name="auth" element={<Auth />} />
            <Route name="app" element={<App />} />
          </Route>
        </Routes>
      </Router>
    </>
  )
}
